import { useState, useMemo } from "react";
import { TrendingUp, Calendar, Building2, Target, Sparkles } from "lucide-react";
import { COUNTRY_ROI_BENCHMARKS, UPLIFT_SCENARIOS, ADOPTION_SCENARIOS } from "@/data/buOwnerData";

const Field = ({ label, value, setValue, min, max, step, suffix, testid, icon: Icon }) => (
  <div className="bg-white border border-[#e8e8e8] rounded-sm p-5">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-[#dd260e]" strokeWidth={1.8} />
      <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#555555]">{label}</label>
    </div>
    <div className="flex items-baseline gap-2 mb-3">
      <span className="font-condensed text-3xl text-[#000000] tabular-nums">
        {value.toLocaleString()}
      </span>
      <span className="text-xs text-[#555555] font-semibold">{suffix}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => setValue(Number(e.target.value))}
      className="w-full accent-[#dd260e] cursor-pointer"
      data-testid={`roi-input-${testid}`}
    />
    <div className="flex justify-between mt-1">
      <span className="text-[9px] text-[#555555]">{min.toLocaleString()}</span>
      <span className="text-[9px] text-[#555555]">{max.toLocaleString()}</span>
    </div>
  </div>
);

export const CountryRoiCalculator = () => {
  const [outlets, setOutlets] = useState(1500);
  const [revenuePerOutlet, setRevenuePerOutlet] = useState(90000);
  const [adoption, setAdoption] = useState(ADOPTION_SCENARIOS.realistic.value);
  const [horizon, setHorizon] = useState(24);
  const [scenarioKey, setScenarioKey] = useState("midpoint");

  const scenario = UPLIFT_SCENARIOS[scenarioKey];

  const numbers = useMemo(() => {
    const activeOutlets = Math.round(outlets * (adoption / 100));
    const annualUpliftPerOutlet = revenuePerOutlet * scenario.value;
    const totalAnnualUplift = activeOutlets * annualUpliftPerOutlet;
    const totalHorizonUplift = (totalAnnualUplift * horizon) / 12;
    return {
      activeOutlets,
      totalAnnualUplift,
      totalHorizonUplift,
      paybackMin: COUNTRY_ROI_BENCHMARKS.paybackMinMonths,
      paybackMax: COUNTRY_ROI_BENCHMARKS.paybackMaxMonths,
    };
  }, [outlets, revenuePerOutlet, adoption, horizon, scenario]);

  const formatEur = (n) =>
    new Intl.NumberFormat("en-EU", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(n);

  return (
    <div className="mb-14" data-testid="roi-calculator-section">
      <div className="mb-6 pb-4 border-b border-[#e8e8e8]">
        <span className="cchbc-eyebrow mb-3">Country-level Business Case</span>
        <h2 className="font-condensed text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#000000] mt-3">
          What FORA could mean for your country
        </h2>
        <p className="text-sm text-[#555555] mt-2 max-w-2xl leading-relaxed">
          Move the sliders. Defaults are CC HBC averages; uplift band &mdash; <strong className="text-[#000000]">+12% to +22%</strong> &mdash; comes from live HR / PL / SI deployments. Pick a scenario to see how it changes the projection.
        </p>
      </div>

      {/* Scenario toggle */}
      <div className="mb-6" data-testid="roi-scenario-toggle">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#555555] mb-3">
          Uplift scenario &mdash; choose how aggressive
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {Object.entries(UPLIFT_SCENARIOS).map(([key, sc]) => {
            const active = scenarioKey === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setScenarioKey(key)}
                className={`text-left p-4 rounded-sm border transition-colors ${
                  active
                    ? "bg-[#000000] border-[#000000] text-white"
                    : "bg-white border-[#e8e8e8] text-[#555555] hover:border-[#000000] hover:text-[#000000]"
                }`}
                data-testid={`roi-scenario-${key}`}
              >
                <div className="flex items-baseline justify-between mb-2">
                  <span className={`text-[10px] font-bold uppercase tracking-[0.15em] ${active ? "text-white/60" : "text-[#555555]"}`}>{sc.label}</span>
                  <span className={`font-condensed text-3xl tabular-nums ${active ? "text-[#dd260e]" : "text-[#000000]"}`}>
                    +{Math.round(sc.value * 100)}%
                  </span>
                </div>
                <p className={`text-[10px] leading-snug ${active ? "text-white/70" : "text-[#555555]"}`}>{sc.note}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Field
          label="Outlets in your country"
          value={outlets}
          setValue={setOutlets}
          min={100}
          max={10000}
          step={50}
          suffix="outlets"
          testid="outlets"
          icon={Building2}
        />
        <Field
          label="Avg revenue per outlet"
          value={revenuePerOutlet}
          setValue={setRevenuePerOutlet}
          min={30000}
          max={500000}
          step={5000}
          suffix="€ / year"
          testid="revenue"
          icon={TrendingUp}
        />
        <Field
          label="Target adoption %"
          value={adoption}
          setValue={setAdoption}
          min={10}
          max={90}
          step={1}
          suffix="% of outlets"
          testid="adoption"
          icon={Target}
        />
        <Field
          label="Time horizon"
          value={horizon}
          setValue={setHorizon}
          min={12}
          max={48}
          step={6}
          suffix="months"
          testid="horizon"
          icon={Calendar}
        />
      </div>

      {/* Adoption preset helper */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="adoption-presets">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#555555] shrink-0">
          Adoption rate &mdash; pick a preset
        </span>
        <div className="flex flex-wrap gap-2">
          {Object.entries(ADOPTION_SCENARIOS).map(([key, sc]) => {
            const active = adoption === sc.value;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setAdoption(sc.value)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${
                  active
                    ? "bg-[#000000] border-[#000000] text-white"
                    : "bg-white border-[#e8e8e8] text-[#555555] hover:border-[#000000] hover:text-[#000000]"
                }`}
                data-testid={`adoption-preset-${key}`}
              >
                {key === "aspirational" && <Sparkles className="w-3 h-3 text-[#dd260e]" />}
                <span>{sc.label}</span>
                <span className={`tabular-nums ${active ? "text-[#dd260e]" : "text-[#000000]"}`}>{sc.value}%</span>
                <span className={`hidden sm:inline ${active ? "text-white/60" : "text-[#555555]"}`}>&middot; {sc.note}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Outputs */}
      <div className="bg-[#000000] rounded-sm p-8 sm:p-10">
        <span className="cchbc-eyebrow light mb-4">Projected Outcome</span>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-4 lg:gap-8 mt-6">
          <div className="min-w-0" data-testid="roi-output-uplift">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 mb-3">
              Total revenue uplift &mdash; {horizon} months
            </p>
            <p className="font-condensed text-3xl lg:text-4xl xl:text-5xl text-white tabular-nums leading-none break-words">
              {formatEur(numbers.totalHorizonUplift)}
            </p>
            <p className="text-[11px] text-white/40 mt-2">
              {formatEur(numbers.totalAnnualUplift)} / year recurring
            </p>
          </div>
          <div className="min-w-0" data-testid="roi-output-outlets">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 mb-3">
              Outlets activated
            </p>
            <p className="font-condensed text-3xl lg:text-4xl xl:text-5xl text-white tabular-nums leading-none">
              {numbers.activeOutlets.toLocaleString()}
            </p>
            <p className="text-[11px] text-white/40 mt-2">
              {adoption}% of {outlets.toLocaleString()} eligible
            </p>
          </div>
          <div className="min-w-0" data-testid="roi-output-payback">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 mb-3">
              Avg. payback per outlet
            </p>
            <p className="font-condensed text-3xl lg:text-4xl xl:text-5xl text-white tabular-nums leading-none">
              {numbers.paybackMin}&ndash;{numbers.paybackMax}
              <span className="text-lg lg:text-xl text-white/60 ml-1">months</span>
            </p>
            <p className="text-[11px] text-white/40 mt-2">Range from HR / PL / SI cohorts</p>
          </div>
        </div>
        <p className="text-[10px] text-white/40 italic mt-8">
          Indicative model &mdash; current scenario: <strong className="text-white/70">{scenario.label} (+{Math.round(scenario.value * 100)}%)</strong> at <strong className="text-white/70">{adoption}% adoption</strong>. Uplift band +12% / +18% / +22% reflects HR / PL / SI variance. Adoption defaults to 42% (Year-1 realistic); 65% is the Croatia pace (14 months, strong BU push). Revenue per outlet defaults to <strong className="text-white/70">&euro;90k</strong> &mdash; representative of the full CC HBC outlet base (small bars, caf&eacute;s, kiosks). Premium / high-volume venues reach &euro;180k+.
        </p>
      </div>
    </div>
  );
};
